import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { IndianRupee, CheckCircle2, RotateCcw, Copy, Mail, PauseCircle, PlayCircle, AlertTriangle, CalendarClock, Flag } from "lucide-react";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// UPI RRN = 12 digits · bank UTR (IMPS/NEFT/RTGS) = 16–22 alphanumerics
export const UTR_RE = /^(?:\d{12}|[A-Za-z0-9]{16,22})$/;
export const UTR_ERROR = "Please check the UTR/UPI reference and enter the correct one before marking as paid (UPI ref = 12 digits, bank UTR = 16–22 letters/digits).";

const FILTERS = [["due", "Due today / overdue"], ["pending", "All pending"], ["hold", "On hold"], ["paid", "Paid"], ["all", "All"]];

export function copyText(t, label = "Copied") {
  navigator.clipboard?.writeText(String(t)).then(() => toast.success(label)).catch(() => toast.error("Copy failed"));
}

export default function PayoutsPanel({ initialFilter = "due" }) {
  const [status, setStatus] = useState(initialFilter);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");
  const [hold, setHold] = useState(null);
  const [holdReason, setHoldReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [remBusy, setRemBusy] = useState(false);

  const load = () => api.get(`/admin/payouts?status=${status}`).then(({ data }) => setData(data)).catch(() => toast.error("Could not load payouts"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const refClean = ref.replace(/\s+/g, "");
  const refValid = UTR_RE.test(refClean);

  const markPaid = async () => {
    if (!refValid) { toast.error(UTR_ERROR); return; }
    setBusy(true);
    try {
      await api.post(`/admin/payouts/${sel.booking_id}/mark-paid`, { reference: refClean, note });
      toast.success("Payout marked as paid — owner has been emailed");
      setSel(null); setRef(""); setNote(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doHold = async () => {
    if (holdReason.trim().length < 3) { toast.error("Please give a reason for the hold"); return; }
    setBusy(true);
    try { await api.post(`/admin/payouts/${hold.booking_id}/hold`, { reason: holdReason }); toast.success("Payout put on hold"); setHold(null); setHoldReason(""); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const act = async (path, msg) => {
    try { await api.post(path); toast.success(msg); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const sendReminder = async () => {
    setRemBusy(true);
    try {
      const { data } = await api.post("/admin/payouts/send-reminder");
      toast.success(data.count ? `Reminder emailed to ${data.to} — ${data.count} payout(s), ${inr(data.total_inr)}` : `No payouts pending > 3 days — empty summary sent to ${data.to}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setRemBusy(false); }
  };

  const c = data?.counts;
  return (
    <section className="mt-14" data-testid="admin-payouts">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Manual payouts</div>
          <h2 className="font-display text-3xl mt-1">Pending payouts to owners</h2>
          <p className="text-sm text-[#5C6B7A] mt-1">Tenant paid via Cashfree → transfer to the owner&rsquo;s bank/UPI within {data?.due_days || 5} days → enter UTR → mark as paid. Tap any number to open its list.</p>
        </div>
        <Button size="sm" variant="outline" onClick={sendReminder} disabled={remBusy} className="rounded-md" data-testid="payouts-send-reminder">
          <Mail size={12} className="mr-1" /> {remBusy ? "Sending…" : "Email reminder now"}
        </Button>
      </div>

      {c && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="payouts-summary">
          <SummaryBtn active={status === "due"} onClick={() => setStatus("due")} label="Due today / overdue" v={c.due} sub={`${inr(c.due_paise / 100)} · ${c.overdue} overdue`} tone="hot" testId="payouts-sum-due" />
          <SummaryBtn active={status === "pending"} onClick={() => setStatus("pending")} label="Pending" v={c.pending} sub="incl. upcoming" testId="payouts-sum-pending" />
          <SummaryBtn active={status === "pending"} onClick={() => setStatus("pending")} label="To transfer" v={inr(c.pending_paise / 100)} sub="breakdown below" testId="payouts-sum-amount" />
          <SummaryBtn active={status === "hold"} onClick={() => setStatus("hold")} label="On hold" v={c.on_hold} sub="under review" testId="payouts-sum-hold" />
          <SummaryBtn active={status === "paid"} onClick={() => setStatus("paid")} label="Paid" v={c.paid} sub="history" testId="payouts-sum-paid" />
        </div>
      )}

      <div className="mt-4 inline-flex flex-wrap rounded-md bg-[#F0EBE1] p-1 text-sm" data-testid="payouts-filter">
        {FILTERS.map(([s, l]) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded ${status === s ? "bg-[#FDFBF7] shadow-sm font-semibold" : "text-[#5C6B7A]"}`} data-testid={`payouts-filter-${s}`}>{l}</button>
        ))}
      </div>

      {!data ? <div className="mt-4 text-sm text-[#5C6B7A]">Loading…</div> : data.items.length === 0 ? (
        <div className="mt-4 bg-[#F0EBE1] rounded-lg p-8 text-center text-[#5C6B7A]" data-testid="payouts-empty">
          {status === "due" ? "Nothing due today — you're all caught up." : `No ${status === "all" ? "" : status} payouts.`}
        </div>
      ) : (
        <div className="mt-4 space-y-3" data-testid="payouts-list">
          {data.items.map((p) => <PayoutRow key={p.booking_id} p={p} onPaid={() => setSel(p)} onHold={() => setHold(p)} onRelease={() => act(`/admin/payouts/${p.booking_id}/release`, "Hold released — payout is pending again")} onReopen={() => act(`/admin/payouts/${p.booking_id}/mark-pending`, "Payout reopened")} />)}
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent data-testid="payout-dialog">
          <DialogHeader>
            <DialogTitle>Mark payout as paid</DialogTitle>
            <DialogDescription>Confirm you transferred <b>{sel ? inr(sel.amount_paise / 100) : ""}</b> to <b>{sel?.owner?.name}</b>{sel?.owner?.bank?.upi_id ? ` (UPI ${sel.owner.bank.upi_id})` : sel?.owner?.bank?.account_number ? ` (A/c …${String(sel.owner.bank.account_number).slice(-4)})` : ""}. The owner gets an email with this reference.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Input placeholder="UTR / UPI reference number (required)" value={ref} onChange={(e) => setRef(e.target.value)} className={ref && !refValid ? "border-red-500 focus-visible:ring-red-500" : ""} data-testid="payout-ref" />
            {ref && !refValid && <div className="text-xs text-red-600 flex items-start gap-1" data-testid="payout-ref-error"><AlertTriangle size={13} className="shrink-0 mt-0.5" /> {UTR_ERROR}</div>}
            {refValid && <div className="text-xs text-[#2B3A67]" data-testid="payout-ref-ok">✓ Looks like a valid {/^\d{12}$/.test(refClean) ? "UPI reference (12 digits)" : "bank UTR"}</div>}
            <Textarea placeholder="Internal note (optional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="payout-note" />
          </div>
          <DialogFooter>
            <Button onClick={markPaid} disabled={busy || !refValid} className="bg-[#2B3A67] hover:bg-[#1f2c52] text-white" data-testid="payout-confirm">{busy ? "Saving…" : "Confirm paid"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!hold} onOpenChange={(v) => !v && setHold(null)}>
        <DialogContent data-testid="hold-dialog">
          <DialogHeader>
            <DialogTitle>Put payout on hold</DialogTitle>
            <DialogDescription>{hold ? `${inr(hold.amount_paise / 100)} to ${hold.owner?.name} · ${hold.agreement_number}` : ""}. It will stop showing as due until you release it.</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={holdReason} onChange={(e) => setHoldReason(e.target.value)} placeholder="Reason — e.g. tenant raised dispute #ab12cd34, verifying with owner" data-testid="hold-reason" />
          <DialogFooter><Button onClick={doHold} disabled={busy} className="bg-[#C85A32] hover:bg-[#a8482a] text-white" data-testid="hold-confirm">{busy ? "Saving…" : "Put on hold"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function SummaryBtn({ label, v, sub, onClick, active, tone, testId }) {
  return (
    <button type="button" onClick={onClick} className={`text-left rounded-lg border p-3 transition-all hover:shadow-md ${active ? "border-[#C85A32] bg-[#FDFBF7]" : "border-[#1A1F2C]/10 bg-[#F0EBE1] hover:border-[#C85A32]/50"}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B7A]">{label}</div>
      <div className={`font-display text-2xl mt-0.5 ${tone === "hot" ? "text-[#C85A32]" : "text-[#2B3A67]"}`}>{v}</div>
      <div className="text-[11px] text-[#5C6B7A]">{sub} →</div>
    </button>
  );
}

function DueBadge({ p }) {
  const m = {
    overdue: ["bg-red-600 text-white", `Overdue by ${Math.abs(p.days_left)}d`],
    due_today: ["bg-[#C85A32] text-white", "Due today"],
    upcoming: ["bg-[#D4DEC6] text-[#2B3A67]", `Due in ${p.days_left}d`],
    on_hold: ["bg-[#1A1F2C] text-white", "On hold"],
    paid: ["bg-[#D4DEC6] text-[#2B3A67]", "Paid"],
  }[p.due_status] || ["bg-[#F0EBE1]", p.due_status];
  return <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.16em] inline-flex items-center gap-1 ${m[0]}`} data-testid={`payout-due-${p.booking_id}`}><CalendarClock size={10} /> {m[1]}</span>;
}

function PayoutRow({ p, onPaid, onHold, onRelease, onReopen }) {
  const bank = p.owner.bank;
  return (
    <div className={`rounded-lg border bg-[#F0EBE1] p-4 grid md:grid-cols-[1fr_1fr_auto] gap-4 ${p.payout_status === "on_hold" ? "border-[#1A1F2C]/60" : p.due_status === "overdue" ? "border-red-400" : "border-[#1A1F2C]/10"}`} data-testid={`payout-${p.booking_id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.16em] ${p.payout_status === "paid" ? "bg-[#D4DEC6] text-[#2B3A67]" : p.payout_status === "on_hold" ? "bg-[#1A1F2C] text-white" : "bg-[#C85A32]/15 text-[#C85A32]"}`} data-testid={`payout-status-${p.booking_id}`}>{p.payout_status.replace("_", " ")}</span>
          <DueBadge p={p} />
          {p.open_dispute_id && <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.16em] bg-red-100 text-red-700 inline-flex items-center gap-1" data-testid={`payout-dispute-${p.booking_id}`}><Flag size={10} /> Dispute open</span>}
          <span className="font-display text-xl inline-flex items-center"><IndianRupee size={15} />{(p.amount_paise / 100).toLocaleString("en-IN")}</span>
        </div>
        <Link to={`/property/${p.property_id}`} className="block mt-1 font-medium hover:text-[#C85A32] truncate">{p.property_title}</Link>
        <div className="text-xs text-[#5C6B7A]"><Link to={`/bookings/${p.booking_id}`} className="underline hover:text-[#C85A32]">{p.agreement_number}</Link> · tenant {p.tenant_name} · paid {new Date(p.paid_at).toLocaleDateString()} · due {new Date(p.due_at).toLocaleDateString()}</div>
        <div className="text-xs text-[#5C6B7A]">via {p.provider || "—"}{p.provider_order_id ? ` · ${p.provider_order_id}` : ""}</div>
        {p.payout_status === "on_hold" && <div className="mt-1.5 text-xs text-[#1A1F2C] bg-[#FDFBF7] border border-[#1A1F2C]/20 rounded px-2 py-1" data-testid={`payout-hold-reason-${p.booking_id}`}><PauseCircle size={11} className="inline mr-1" />Hold: {p.payout_hold_reason} <span className="text-[#5C6B7A]">({p.payout_hold_at ? new Date(p.payout_hold_at).toLocaleString() : ""})</span></div>}
        {p.payout_status === "paid" && <div className="text-xs mt-1 text-[#2B3A67]" data-testid={`payout-paidinfo-${p.booking_id}`}>Transferred {p.payout_paid_at ? new Date(p.payout_paid_at).toLocaleString() : ""} · UTR/ref <span className="font-mono">{p.payout_reference}</span>{p.payout_note ? ` · ${p.payout_note}` : ""}</div>}
      </div>
      <div className="text-sm min-w-0" data-testid={`payout-owner-${p.booking_id}`}>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6B7A]">Transfer to</div>
        <div className="font-medium">{p.owner.name}{p.owner.business_name ? ` · ${p.owner.business_name}` : ""}</div>
        <div className="text-xs text-[#5C6B7A]">{p.owner.email}{p.owner.phone ? ` · ${p.owner.phone}` : ""}</div>
        {bank ? (
          <div className="mt-1.5 space-y-1 text-xs">
            <div className="text-[#5C6B7A]">{bank.account_holder_name}{bank.bank_name ? ` · ${bank.bank_name}` : ""}</div>
            <CopyRow label="A/c" value={bank.account_number} extra={bank.ifsc_code} testId={`copy-account-${p.booking_id}`} missing="No bank account saved" />
            <CopyRow label="UPI" value={bank.upi_id} testId={`copy-upi-${p.booking_id}`} missing="No UPI ID saved" />
          </div>
        ) : (
          <div className="mt-1.5 text-xs text-red-600" data-testid={`payout-nobank-${p.booking_id}`}>Owner hasn&rsquo;t added bank/UPI details yet — ask them to add it in Profile.</div>
        )}
      </div>
      <div className="flex md:flex-col gap-2 justify-end md:justify-center">
        {p.payout_status === "paid" ? (
          <Button size="sm" variant="outline" onClick={onReopen} className="rounded-md" data-testid={`payout-reopen-${p.booking_id}`}><RotateCcw size={12} className="mr-1" /> Reopen</Button>
        ) : p.payout_status === "on_hold" ? (
          <Button size="sm" variant="outline" onClick={onRelease} className="rounded-md" data-testid={`payout-release-${p.booking_id}`}><PlayCircle size={12} className="mr-1" /> Release hold</Button>
        ) : (
          <>
            <Button size="sm" onClick={onPaid} className="bg-[#2B3A67] hover:bg-[#1f2c52] text-white rounded-md" data-testid={`payout-markpaid-${p.booking_id}`}><CheckCircle2 size={12} className="mr-1" /> Mark as paid</Button>
            <Button size="sm" variant="outline" onClick={onHold} className="rounded-md" data-testid={`payout-hold-${p.booking_id}`}><PauseCircle size={12} className="mr-1" /> Hold</Button>
          </>
        )}
      </div>
    </div>
  );
}

function CopyRow({ label, value, extra, testId, missing }) {
  if (!value) return <div className="text-[#5C6B7A] italic">{label}: {missing}</div>;
  return (
    <div className="flex items-center gap-1.5 font-mono">
      <span className="font-sans text-[#5C6B7A]">{label}</span> {value}{extra ? ` · ${extra}` : ""}
      <button type="button" onClick={() => copyText(value, `${label} copied`)} className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-[#1A1F2C]/15 bg-[#FDFBF7] text-[#2B3A67] hover:text-[#C85A32] font-sans" aria-label={`copy ${label}`} data-testid={testId}><Copy size={11} /> copy</button>
    </div>
  );
}
